/**
 * Keyboard transport for motion mode. Pure, so the editor only has to
 * perform the action.
 *
 * In motion mode these take the keys that tools otherwise own (J / K / L),
 * because a timeline needs shuttle keys and the tools stay on the toolbar.
 * `[` / `]` trim: on a selected clip they move its in and out points, and
 * Shift extends instead of trimming. With no clip selected they shorten (`[`)
 * or lengthen (`]`) the open scene.
 */

import type { VideoClipRef } from '@canvas/shared-types';
import { clampClip } from './videoClip';

export interface MotionKey {
  code: string;
  shiftKey: boolean;
  repeat: boolean;
}

export type MotionShortcutAction =
  | { kind: 'step'; deltaFrames: number }
  | { kind: 'play' }
  | { kind: 'pause' }
  | { kind: 'trim'; edge: 'in' | 'out'; shorten: boolean };

export function motionShortcutFor(key: MotionKey): MotionShortcutAction | null {
  switch (key.code) {
    case 'Comma':
    case 'KeyJ':
      return { kind: 'step', deltaFrames: -1 };
    case 'Period':
      return { kind: 'step', deltaFrames: 1 };
    case 'BracketLeft':
      return { kind: 'trim', edge: 'in', shorten: !key.shiftKey };
    case 'BracketRight':
      return { kind: 'trim', edge: 'out', shorten: !key.shiftKey };
    case 'KeyK':
      return key.repeat ? null : { kind: 'pause' };
    case 'KeyL':
      return key.repeat ? null : { kind: 'play' };
    default:
      return null;
  }
}

/** Move a clip's in or out point by one step. `shorten` eats the clip; Shift passes false. */
export function trimClipBy(
  clip: VideoClipRef,
  edge: 'in' | 'out',
  shorten: boolean,
  frameMs: number
): VideoClipRef {
  const step = Math.abs(frameMs);
  if (edge === 'in') {
    return clampClip({ ...clip, trimStartMs: clip.trimStartMs + (shorten ? step : -step) });
  }
  return clampClip({ ...clip, trimEndMs: clip.trimEndMs + (shorten ? -step : step) });
}

/**
 * Next scene duration for a trim key. `[` shortens and `]` lengthens, so the
 * out edge grows when it is "trimming" — the scene's only edge is its end.
 */
export function trimSceneBy(
  durationMs: number,
  edge: 'in' | 'out',
  shorten: boolean,
  frameMs: number
): number {
  const grow = edge === 'out' ? shorten : !shorten;
  return durationMs + (grow ? Math.abs(frameMs) : -Math.abs(frameMs));
}
